/**
 * DatePickerField - Reusable date picker component for shipment forms
 * Extracted from AddLoadSheet.js and EditLoadSheet.js
 * 
 * Shows today's date by default when no value is set, but doesn't auto-fill.
 * User can change the date as needed.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, isValid } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

/**
 * Helper to check if a date value is valid
 * Returns null for invalid/empty dates, otherwise returns a valid Date object
 */
const getValidDate = (value) => {
  if (!value) return null;
  
  // If it's already a Date object, check if it's valid
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }
  
  // If it's a string, try to parse it
  if (typeof value === 'string') {
    const trimmed = value.trim();
    // Handle empty strings or invalid string values
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined' || trimmed === 'None') {
      return null;
    }
    const parsed = new Date(trimmed);
    return isValid(parsed) ? parsed : null;
  }
  
  return null;
};

export const DatePickerField = ({ 
  label, 
  value, 
  onChange, 
  field,
  placeholder = 'Pick a date',
  className = '',
  showTodayByDefault = true  // Show today's date as default display
}) => {
  const validDate = getValidDate(value);
  const [isOpen, setIsOpen] = useState(false);
  
  // When no date is set and showTodayByDefault is true, display today's date
  // but still allow user to change it
  const displayDate = validDate;
  const today = new Date();
  
  // Handle date selection - always set to selected date
  const handleSelect = (date) => {
    onChange(field, date);
    setIsOpen(false);
  };
  
  // When popover opens and no date is set, auto-select today
  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (open && !validDate && showTodayByDefault) {
      // Auto-set to today when opening if no value
      onChange(field, today);
    }
  };
  
  return (
    <div className={`space-y-2 ${className}`}>
      {label && <Label className="text-slate-300">{label}</Label>}
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-start text-left font-normal bg-slate-950 border-slate-800 text-white hover:bg-slate-900"
            data-testid={`datepicker-${field}`}
          >
            <CalendarIcon className="mr-2 h-4 w-4 text-slate-500" />
            {displayDate ? (
              format(displayDate, 'MM/dd/yyyy')
            ) : showTodayByDefault ? (
              <span className="text-slate-400">{format(today, 'MM/dd/yyyy')}</span>
            ) : (
              <span className="text-slate-500">{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-800 z-[100]" align="start">
          <Calendar
            mode="single"
            selected={displayDate || today}
            onSelect={handleSelect}
            initialFocus
            className="bg-slate-900"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DatePickerField;
